import {useEffect, useState} from "react"
import {Navigate, useRouteError, redirect} from "react-router-dom"
import {PrimeReactProvider} from 'primereact/api';
import PrimeReact from 'primereact/api';

import {Root} from "./index"
import {StableNavigateContextProvider} from "./utilities/StableNavigateContext"
import {bootstrapFromCookie, getToken} from "./utilities/useToken"
import ErrorPage, {Error404Page} from "./components/ErrorPage"
import AuthenticatedRoot, {fetchData as fetchAuthenticatedRoot} from "./components/AuthenticatedRoot"
import Login from "./components/Login"
import Logout from "./components/Logout"
import AdoptPersona from "./views/AdoptPersona"
import Home from "./components/Home"
import HomeOrientee from "./views/HomeOrientee"
import MetricsHome from "./components/metrics/MetricsHome"
import MetricsDashboard from "./components/metrics/dashboard/MetricsDashboard"
import Help from "./components/Help"
import Users from "./components/admin/Users"
import NewOrientation from "./views/admin/NewOrientation"
import NewHireStatusReportSelection from "./views/surveys/NewHireStatusReportSelection"
import NewHireStatusReport from "./views/surveys/NewHireStatusReport"
import OrientationStatusReportSelection from "./views/surveys/OrientationStatusReportSelection"
import OrientationStatusReport from "./views/surveys/OrientationStatusReport"
import OrganizationList from "./views/admin/OrganizationList"
import Organization from "./views/admin/Organization"
import Facility from "./views/admin/Facility"
import Unit from "./views/admin/Unit"
import PreceptorOrientations from "./views/PreceptorOrientations"
import PreceptorCompletedOrientations from "./views/PreceptorCompletedOrientations"
import PreceptorMetrics from "./views/PreceptorMetrics"
import EducatorOrientations from "./views/EducatorOrientations"
import EducatorOrientationDetails from "./views/EducatorOrientationDetails"
import EditUserPage from "./components/admin/EditUserPage"
import EducatorOnboarding from "./views/EducatorOnboarding"
import EducatorOnboardingWizard from "./views/EducatorOnboardingWizard"
import EducatorOrienteeStatus from "./components/educator/EducatorOrienteeStatus"
import {EducatorProgressReport} from "./components/educator/EducatorProgressReport"
import EducatorAssignPreceptorsPage from "./components/educator/EducatorAssignPreceptorsPage"
import MobileMenuPage from "./views/MobileMenuPage"

import {checkPreceptorAccess} from "./loaders/checkPreceptorAccess"
import {checkUserAdminAccess} from "./loaders/checkUserAdminAccess"
import {checkSurveysAccess} from "./loaders/checkSurveysAccess"
import {checkEducatorOrientationsAccess} from "./loaders/checkEducatorOrientationsAccess"
import EducatorPreceptorMetrics from "./views/EducatorPreceptorMetrics"

PrimeReact.hideOverlaysOnDocumentScrolling = true;

function LoginWrapper() {
    const [checking, setChecking] = useState(true)
    const [hasToken, setHasToken] = useState(false)

    useEffect(() => {
        const init = async () => {
            const {access_token} = getToken(true)
            if (access_token) {
                setHasToken(true)
                setChecking(false)
                return
            }
            const user = await bootstrapFromCookie()
            setHasToken(!!user)
            setChecking(false)
        }
        init()
    }, [])

    if (checking) {
        return null // or a Spinner if you want
    }
    return hasToken ? <Navigate to="/home" /> : <Login />
}
function RootWrapper() {
    // StableNavigateContextProvider must be within RouterProvider but outside of the Root
    return (
        <StableNavigateContextProvider>
            <PrimeReactProvider>
                <Root />
            </PrimeReactProvider>
        </StableNavigateContextProvider>
    )
}
function ErrorWrapper() {
    const error = useRouteError()
    console.error(error)

    if (error.status === 404) {
        return <Error404Page fullpage />
    } else if (error.response?.status === 401) {
        // Interceptor will handle redirect, just render nothing
        return null
    } else {
        return <ErrorPage fullpage />
    }
}
function AuthWrapper(func) {
    async function wrapped() {
        let {access_token} = getToken(true)
        if (!access_token) {
            // Try OIDC cookie before giving up
            const user = await bootstrapFromCookie()
            if (!user) {
                // Remove access_token to prevent edge case where this is set but the actual token isn't
                sessionStorage.removeItem("auth_method")
                return redirect(
                    "/login?redirectTo=" + encodeURIComponent(window.location.pathname + window.location.search)
                )
            }
            access_token = sessionStorage.getItem("access_token")
        }
        return func.apply(this, arguments)
    }
    return wrapped
}

export const routes = [
    {
        element: <RootWrapper />,
        errorElement: <ErrorWrapper />,
        children: [
            {path: "/login?", element: <LoginWrapper />},
            {path: "/logoutnav", element: <Logout />},
            {
                id: "AuthenticatedRoot",
                element: <AuthenticatedRoot />,
                loader: AuthWrapper(fetchAuthenticatedRoot),
                // This might not be necessary if actions are added to this list
                shouldRevalidate: () => true,
                children: [
                    {path: "/adopt-persona", element: <AdoptPersona />},
                    {path: "/home", element: <Home />},
                    {path: "/menu", element: <MobileMenuPage />, hideNavBar: true, hideFooter: true},
                    {path: "/home-orientee", element: <HomeOrientee />},
                    {path: "/preceptor-orientations", element: <PreceptorOrientations />},
                    {path: "/preceptor-completed-orientations", element: <PreceptorCompletedOrientations />},
                    {
                        path: "/preceptor-metrics",
                        element: <PreceptorMetrics />,
                    },
                    {
                        path: "/educator-orientations",
                        loader: checkEducatorOrientationsAccess,
                        children: [
                            {
                                index: true,
                                element: <EducatorOrientations />,
                            },
                            {
                                path: ":orientation_id",
                                element: <EducatorOrientationDetails />,
                                hideNavBar: true,
                                hideFooter: true,
                            },
                            {
                                path: ":orientation_id/status",
                                element: <EducatorOrienteeStatus />,
                            },
                            {
                                path: ":orientation_id/progress",
                                element: <EducatorProgressReport />,
                            },
                            {
                                path: ":orientation_id/preceptors",
                                element: <EducatorAssignPreceptorsPage />,
                            },
                        ],
                    },
                    {path: "/educator-onboarding", element: <EducatorOnboarding />},
                    {
                        path: "/educator-preceptor-metrics", 
                        // Disabled until next release — redirect everyone away for now.
                        // loader: () => {
                        //     const {role} = getToken(true) || {}
                        //     return role === "Educator" ? redirect("/educator-orientations") : redirect("/home")
                        // },
                        element: <EducatorPreceptorMetrics />},
                    {
                        path: "/educator-onboarding/:orientation_id",
                        element: <EducatorOnboardingWizard />,
                        hideFooter: true,
                        hideNavBar: true,
                    },
                    {
                        path: "/surveys",
                        loader: checkSurveysAccess,
                        children: [
                            {
                                index: true,
                                element: <Navigate to="home" replace />,
                            },
                            {
                                path: "new-hire-status-report-selection",
                                element: <NewHireStatusReportSelection />,
                                hideFooter: true,
                            },
                            {
                                path: "new-hire-status-report/:responseId",
                                element: <NewHireStatusReport />,
                                hideNavBar: true,
                                hideFooter: true,
                            },
                            {
                                path: "orientation-status-report-selection",
                                loader: checkPreceptorAccess,
                                element: <OrientationStatusReportSelection />,
                            },
                            {
                                path: "orientation-status-report/:responseId",
                                element: <OrientationStatusReport />,
                                hideNavBar: true,
                                hideFooter: true,
                            },
                        ],
                    },
                    {
                        id: "admin-users",
                        path: "/admin/users",
                        loader: checkUserAdminAccess,
                        children: [
                            {index: true, element: <Users />},
                            {path: "new", element: <EditUserPage />},
                            {path: "edit/:id", element: <EditUserPage />},
                        ],
                    },
                    {
                        path: "/admin/organization",
                        element: <OrganizationList />,
                    },
                    {
                        path: "/admin/organization/:organizationId",
                        element: <Organization />,
                    },
                    {
                        path: "/admin/organization/:organizationId/facility/:facilityId",
                        element: <Facility />,
                    },
                    {
                        path: "/admin/organization/:organizationId/facility/:facilityId/unit/:unitId",
                        element: <Unit />,
                    },
                    {
                        path: "/admin/new-hire-onboarding",
                        element: <NewOrientation />,
                    },
                    {
                        path: "/metrics",
                        children: [
                            {
                                index: true,
                                element: <Navigate to="home" replace />,
                            },
                            {
                                path: "home",
                                element: <MetricsHome />,
                            },
                            {
                                path: "dashboard/:tab?",
                                element: <MetricsDashboard />,
                            },
                        ],
                    },
                    {path: "/help", element: <Help />},
                ],
            },
        ],
    },
]
